'use client';

import React, { useRef, useState, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import cn from 'classnames';

export interface ScrollFadeProps {
  className?: string;
  fadeHeight?: number;
  children: React.ReactNode;
}

export const ScrollFade: React.FC<ScrollFadeProps> = ({
  className,
  // change this to control fade height.
  fadeHeight = 64,
  children,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const [fadeTop, setFadeTop] = useState(false);
  const [fadeBottom, setFadeBottom] = useState(false);

  const updateFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;

    setFadeTop(scrollTop > 1);
    setFadeBottom(scrollHeight - scrollTop - clientHeight > 1);
  }, []);

  useLayoutEffect(() => {
    updateFades();
  });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateFades);
    ro.observe(el);
    Array.from(el.children).forEach((child) => ro.observe(child)); // observe children too

    return () => ro.disconnect();
  }, [updateFades]);

  const maskImage = useMemo(() => {
    if (fadeTop && fadeBottom) {
      return `linear-gradient(to bottom, transparent, black ${fadeHeight}px, black calc(100% - ${fadeHeight}px), transparent)`;
    }
    if (fadeTop) {
      return `linear-gradient(to bottom, transparent, black ${fadeHeight}px)`;
    }
    if (fadeBottom) {
      return `linear-gradient(to bottom, black calc(100% - ${fadeHeight}px), transparent)`;
    }
    return undefined;
  }, [fadeTop, fadeBottom, fadeHeight]);

  return (
    <div
      ref={scrollRef}
      className={cn('overflow-y-auto', className)}
      onScroll={updateFades}
      style={{
        // mask-image: standard property (Firefox, modern Chrome)
        maskImage,
        //TODO - TEST on staging
        // -webkit-mask-image: Safari and older Chrome compatibility
        WebkitMaskImage: maskImage,
      }}
    >
      {children}
    </div>
  );
};

export default ScrollFade;
